import React, { useState, useMemo, useEffect, useRef } from 'react';
import EmojiPicker from 'emoji-picker-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { createThread } from '../../services/forumApi';
import { localizedValue } from '../../localization/presentationLanguage';
const forumText = (es, en) => localizedValue({ es, en });

const NAME_MAP = {
  capricornio:'Capricornio', acuario:'Acuario', piscis:'Piscis', aries:'Aries', tauro:'Tauro', geminis:'Géminis',
  cancer:'Cáncer', leo:'Leo', virgo:'Virgo', libra:'Libra', escorpio:'Escorpio', sagitario:'Sagitario'
};

const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 días
const DRAFT_MAX_BODY = 1200;
const DRAFT_MAX_TITLE = 140;

export default function ForumNewThread() {
  const { sign } = useParams();
  const navigate = useNavigate();

  // ===== State
  const [draft, setDraft] = useState({ title: '', body: '' });
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState('');
  const [hoverBack, setHoverBack] = useState(false);
  const [hoverBtn, setHoverBtn]   = useState(false);
  const [titleFocus, setTitleFocus] = useState(false);
  const [bodyFocus, setBodyFocus]   = useState(false);

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showEmojiTitlePicker, setShowEmojiTitlePicker] = useState(false);

  const insertEmoji = (emoji) => {
    const el = bodyRef.current;
    setDraft((d) => {
      const text = d.body || '';
      if (el && typeof el.selectionStart === 'number') {
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const next = text.slice(0, start) + emoji + text.slice(end);
        // Re-posicionar el caret al final del emoji insertado
        setTimeout(() => {
          try {
            el.focus();
            const pos = start + emoji.length;
            el.setSelectionRange(pos, pos);
          } catch {}
        }, 0);
        return { ...d, body: next };
      }
      return { ...d, body: text + emoji };
    });
  };

  const debounceRef = useRef(null);
  const titleRef = useRef(null);
  const bodyRef = useRef(null);
  const titleEmojiRef = useRef(null);
  const bodyEmojiRef = useRef(null);
  const beforeUnloadHasContentRef = useRef(false);

  const insertEmojiTitle = (emoji) => {
    const el = titleRef.current;
    setDraft((d) => {
      const text = d.title || '';
      if (el && typeof el.selectionStart === 'number') {
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const next = text.slice(0, start) + emoji + text.slice(end);
        setTimeout(() => {
          try {
            el.focus();
            const pos = start + emoji.length;
            el.setSelectionRange(pos, pos);
          } catch {}
        }, 0);
        return { ...d, title: next };
      }
      return { ...d, title: text + emoji };
    });
  };

  // ===== Derived
  const maxTitle = 140;
  const titleLen = draft.title?.length || 0;
  const remaining = maxTitle - titleLen;
  const maxBody = 1200;
  const bodyLen = draft.body?.length || 0;
  const bodyRemaining = Math.max(0, maxBody - bodyLen);
  const disabled = busy || !draft.title.trim() || !draft.body.trim() || bodyLen > maxBody;
  const signLabel = useMemo(() => NAME_MAP[sign] || sign || 'signo', [sign]);
  const progressPct = Math.min(100, Math.round((titleLen / maxTitle) * 100));
  const headerTitleStyle = useMemo(() => {
    const text = `Nuevo hilo en ${signLabel}`;
    const len = text.length;
    // Base font-size and progressively reduce for very long labels (mobile safety)
    let fontSize = 'clamp(15px, 3.6vw, 22px)';
    if (len > 28) fontSize = 'clamp(14px, 3.3vw, 21px)';
    if (len > 34) fontSize = 'clamp(13px, 3.0vw, 20px)';
    if (len > 40) fontSize = 'clamp(12px, 2.8vw, 19px)';
    return { ...styles.headerTitle, fontSize };
  }, [signLabel]);

  // ===== Load/Save draft
  useEffect(() => {
    const key = `draft:${sign}`;
    const saved = localStorage.getItem(key);
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved);
      const now = Date.now();
      const ts = Number(parsed?.ts || 0) || 0;
      const expired = !ts || (now - ts > DRAFT_TTL_MS);

      if (expired) {
        try { localStorage.removeItem(key); } catch {}
        return;
      }

      const title = typeof parsed?.title === 'string' ? parsed.title.slice(0, DRAFT_MAX_TITLE) : '';
      const body = typeof parsed?.body === 'string' ? parsed.body.slice(0, DRAFT_MAX_BODY) : '';

      setDraft({ title, body });
    } catch {
      try { localStorage.removeItem(key); } catch {}
    }
  }, [sign]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      const key = `draft:${sign}`;
      const title = String(draft?.title || '').slice(0, DRAFT_MAX_TITLE);
      const body = String(draft?.body || '').slice(0, DRAFT_MAX_BODY);
      const hasContent = title.trim().length > 0 || body.trim().length > 0;

      try {
        if (!hasContent) {
          localStorage.removeItem(key);
          return;
        }

        localStorage.setItem(key, JSON.stringify({
          title,
          body,
          ts: Date.now(),
        }));

        // Limpieza oportunista: borrar drafts vencidos de otras categorías del foro.
        try {
          const now = Date.now();
          for (let i = 0; i < localStorage.length; i += 1) {
            const k = localStorage.key(i);
            if (!k || !k.startsWith('draft:')) continue;
            if (k === key) continue;

            const raw = localStorage.getItem(k);
            if (!raw) continue;

            try {
              const parsed = JSON.parse(raw);
              const ts = Number(parsed?.ts || 0) || 0;
              if (!ts || (now - ts > DRAFT_TTL_MS)) {
                localStorage.removeItem(k);
              }
            } catch {
              localStorage.removeItem(k);
            }
          }
        } catch {}
      } catch {}
    }, 350);

    return () => clearTimeout(debounceRef.current);
  }, [draft, sign]);

  // Track whether there is unpublished content without re-binding the DOM listener on every keystroke
  useEffect(() => {
    beforeUnloadHasContentRef.current = (draft.title.trim().length + draft.body.trim().length) > 0 && !busy;
  }, [draft, busy]);

  // Warn on page unload if there is content not published
  useEffect(() => {
    const handler = (e) => {
      if (beforeUnloadHasContentRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  useEffect(() => {
    const onDown = (e) => {
      const t = titleEmojiRef.current;
      const b = bodyEmojiRef.current;
      if (t && !t.contains(e.target)) setShowEmojiTitlePicker(false);
      if (b && !b.contains(e.target)) setShowEmojiPicker(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setShowEmojiTitlePicker(false);
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  // Auto-grow textarea
  const autoGrow = () => {
    const el = bodyRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(420, el.scrollHeight) + 'px';
  };
  useEffect(() => { autoGrow(); }, []); // on mount
  useEffect(() => { autoGrow(); }, [draft.body]); // when body changes

  const clearDraft = () => {
    setDraft({ title: '', body: '' });
    try { localStorage.removeItem(`draft:${sign}`); } catch {}
  };

  // ===== Submit
  const submit = async (e) => {
    e.preventDefault();
    if (!draft.title.trim() || !draft.body.trim()) return;
    setBusy(true); setErr('');
    try {
      const t = await createThread(sign, { title: draft.title.trim(), body: draft.body.trim() });
      localStorage.removeItem(`draft:${sign}`);
      navigate(`/foro/tema/${t.slug}`);
    } catch (e2) {
      setErr(e2.message || forumText('Error al crear el hilo', 'The thread could not be created'));
    } finally {
      setBusy(false);
    }
  };

  // Dynamic counter styles
  const counterStyle = {
    ...styles.counter,
    background: remaining <= 15 ? 'rgba(239,68,68,0.12)' : 'rgba(218,165,32,0.18)',
    border: `1px solid ${remaining <= 5 ? '#ef4444' : '#DAA520'}`,
    color: remaining <= 5 ? '#b91c1c' : '#4B0082'
  };

  return (
    <HelmetProvider>
      <div style={styles.page}>
        <Helmet>
          <title>{forumText(`Nuevo hilo · ${String(signLabel || '').trim() || 'Foro'} | Zodiacus`, `New thread · ${String(signLabel || '').trim() || 'Forum'} | Zodiacus`)}</title>
          <meta name="robots" content="noindex" />
          <meta name="description" content={forumText(`Creá un nuevo hilo en la categoría ${signLabel} del foro de Zodiacus.`, `Create a new thread in the ${signLabel} category of the Zodiacus forum.`)} />
        </Helmet>

        <div style={styles.shell}>
          {/* Header */}
          <header style={styles.headerBar}>
            <button
              type="button"
              aria-label={forumText('Volver a la categoría', 'Back to category')}
              onClick={() => navigate(`/foro/${sign}`)}
              onMouseEnter={() => setHoverBack(true)}
              onMouseLeave={() => setHoverBack(false)}
              style={{ ...styles.back, ...(hoverBack ? styles.backHover : null) }}
            >
              {forumText('← Volver', '← Back')}
            </button>
            <h1 style={headerTitleStyle}>{forumText('Nuevo hilo en', 'New thread in')} {signLabel}</h1>
            <div aria-live="polite" style={styles.headerRight}>
              <span style={{ display: 'none' }} />
            </div>
          </header>

          {/* Card */}
          <div style={styles.card}>
            {err && <div style={styles.error}>{err}</div>}

            <form onSubmit={submit} style={styles.form}>
              {/* Title */}
              <label htmlFor="threadTitle" style={styles.labelTitle}>{forumText('Título del hilo', 'Thread title')}</label>
              <div style={styles.inputRow}>
                <input
                  ref={titleRef}
                  id="threadTitle"
                  name="title"
                  style={{ ...styles.input, ...(titleFocus ? styles.inputFocus : null) }}
                  maxLength={maxTitle}
                  value={draft.title}
                  onChange={(e)=>setDraft(d => ({ ...d, title: e.target.value }))}
                  onFocus={() => setTitleFocus(true)}
                  onBlur={() => setTitleFocus(false)}
                  placeholder=""
                  autoComplete="on"
                  required
                  aria-describedby="titleHelp titleCount"
                />

                <button
                  type="button"
                  onClick={() => setShowEmojiTitlePicker(v => !v)}
                  aria-label={showEmojiTitlePicker ? forumText('Cerrar emojis del título', 'Close title emoji picker') : forumText('Abrir emojis para el título', 'Open title emoji picker')}
                  title={showEmojiTitlePicker ? forumText('Cerrar emojis del título', 'Close title emoji picker') : forumText('Abrir emojis para el título', 'Open title emoji picker')}
                  style={{
                    position: 'absolute',
                    right: 44,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    border: '1px solid #DAA520',
                    borderRadius: 10,
                    padding: '4px 8px',
                    background: 'linear-gradient(135deg, #FFFBEA, #FFD54F)',
                    cursor: 'pointer',
                    fontWeight: 800,
                    fontSize: 14,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    color: '#3b0c6e'
                  }}
                >
                  😀
                </button>

                {showEmojiTitlePicker && (
                  <div
                    ref={titleEmojiRef}
                    style={{
                      position: 'absolute',
                      zIndex: 20,
                      right: 0,
                      top: 44,
                      width: 'min(420px, 94vw)',
                      maxHeight: '50vh',
                      overflowY: 'auto',
                      background: 'rgba(255,255,255,0.92)',
                      backdropFilter: 'blur(8px) saturate(1.02)',
                      WebkitBackdropFilter: 'blur(8px) saturate(1.02)',
                      border: '1px solid rgba(75,0,130,0.25)',
                      borderRadius: 12,
                      boxShadow: '0 10px 26px rgba(0,0,0,0.25)',
                      padding: 6,
                    }}
                  >
                    <EmojiPicker
                      onEmojiClick={(emojiData) => {
                        insertEmojiTitle(emojiData.emoji || '');
                        setShowEmojiTitlePicker(false);
                      }}
                      searchDisabled={true}
                      autoFocusSearch={false}
                      lazyLoadEmojis={true}
                      skinTonesDisabled={false}
                      emojiStyle="native"
                      theme="light"
                      width="100%"
                    />
                  </div>
                )}

                <span id="titleCount" title={`${remaining} caracteres restantes`} style={counterStyle}>
                  {remaining}
                </span>
              </div>

              {/* Progress */}
              <div style={styles.progressTrack} aria-hidden>
                <div style={{ ...styles.progressBar, width: `${progressPct}%` }} />
              </div>
              <small id="titleHelp" style={styles.help}>{forumText(`Hasta ${maxTitle} caracteres. Sé claro y específico.`, `Up to ${maxTitle} characters. Be clear and specific.`)}</small>

              {/* Body */}
              <label htmlFor="threadBody" style={{...styles.label, marginTop: 10}}>{forumText('Contenido', 'Content')}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => setShowEmojiPicker((v) => !v)}
                  aria-label={showEmojiPicker ? forumText('Cerrar emojis', 'Close emoji picker') : forumText('Abrir emojis', 'Open emoji picker')}
                  title={showEmojiPicker ? forumText('Cerrar emojis', 'Close emoji picker') : forumText('Abrir emojis', 'Open emoji picker')}
                  style={{
                    border: '1px solid #DAA520',
                    borderRadius: 10,
                    padding: '6px 10px',
                    background: 'linear-gradient(135deg, #FFFBEA, #FFD54F)',
                    cursor: 'pointer',
                    fontWeight: 800,
                    fontSize: 16,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    color: '#3b0c6e'
                  }}
                >
                  😀
                </button>
                {showEmojiPicker && (
                  <div
                    ref={bodyEmojiRef}
                    style={{
                      position: 'absolute',
                      zIndex: 20,
                      right: 0,
                      top: 40,
                      width: 'min(420px, 94vw)',
                      maxHeight: '50vh',
                      overflowY: 'auto',
                      background: 'rgba(255,255,255,0.92)',
                      backdropFilter: 'blur(8px) saturate(1.02)',
                      WebkitBackdropFilter: 'blur(8px) saturate(1.02)',
                      border: '1px solid rgba(75,0,130,0.25)',
                      borderRadius: 12,
                      boxShadow: '0 10px 26px rgba(0,0,0,0.25)',
                      padding: 6,
                    }}
                  >
                    <EmojiPicker
                      onEmojiClick={(emojiData) => {
                        insertEmoji(emojiData.emoji || '');
                        setShowEmojiPicker(false);
                      }}
                      searchDisabled={true}
                      autoFocusSearch={false}
                      lazyLoadEmojis={true}
                      skinTonesDisabled={false}
                      emojiStyle="native"
                      theme="light"
                      width="100%"
                    />
                  </div>
                )}
              </div>
              <textarea
                ref={bodyRef}
                id="threadBody"
                name="body"
                style={{ ...styles.textarea, ...(bodyFocus ? styles.textareaFocus : null) }}
                rows={6}
                maxLength={maxBody}
                value={draft.body}
                onChange={(e)=>setDraft(d => ({ ...d, body: e.target.value }))}
                onFocus={() => setBodyFocus(true)}
                onBlur={() => setBodyFocus(false)}
                placeholder={forumText('Contá tu experiencia o pregunta…', 'Share your experience or question…')}
                autoComplete="on"
                required
                onInput={autoGrow}
                onKeyDown={(e)=>{ if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !disabled) submit(e); }}
              />
              <div style={styles.bodyMetaRow}>
                <small style={styles.bodyHelp}>Hasta {maxBody} caracteres.</small>
                <span
                  title={`${bodyRemaining} caracteres restantes`}
                  style={{
                    ...styles.bodyCounter,
                    ...(bodyRemaining <= 50 ? styles.bodyCounterLow : null),
                    ...(bodyRemaining <= 15 ? styles.bodyCounterWarn : null),
                  }}
                >
                  {bodyRemaining}
                </span>
              </div>

              {/* Actions */}
              <div style={styles.actions}>
                <button
                  type="button"
                  onClick={clearDraft}
                  disabled={busy || (!draft.title && !draft.body)}
                  style={{
                    ...styles.secondaryBtn,
                    ...(busy || (!draft.title && !draft.body) ? styles.secondaryBtnDisabled : null)
                  }}
                  title="Borrar borrador"
                >
                  Limpiar borrador
                </button>

                <button
                  type="submit"
                  disabled={disabled}
                  onMouseEnter={() => setHoverBtn(true)}
                  onMouseLeave={() => setHoverBtn(false)}
                  style={{
                    ...styles.btn,
                    ...(disabled ? styles.btnDisabled : null),
                    ...(hoverBtn && !disabled ? styles.btnHover : null)
                  }}
                >
                  {busy ? 'Publicando…' : 'Publicar hilo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </HelmetProvider>
  );
}

const styles = {
  page: {
    minHeight: '100svh',
    padding: 'max(12px, env(safe-area-inset-top)) 12px max(16px, env(safe-area-inset-bottom))',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    background: 'radial-gradient(1200px 600px at 10% -20%, rgba(218,165,32,0.18), transparent), radial-gradient(1200px 600px at 110% 120%, rgba(75,0,130,0.22), transparent), linear-gradient(180deg, #0b0720 0%, #120a2e 100%)'
  },
  shell: {
    width: '100%',
    maxWidth: 980,
  },
  headerBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 14px',
    background: 'linear-gradient(135deg, rgba(75,0,130,0.96), rgba(35,17,85,0.92))',
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,0.10)',
    boxShadow: '0 16px 38px rgba(0,0,0,0.35)',
    marginBottom: 12,
    color: '#DAA520',
    fontFamily: '"Cinzel Decorative","Cinzel",serif',
    minWidth: 0,
    position: 'relative',
  },
  back: {
    border: 'none',
    background: 'rgba(255,255,255,0.14)',
    color: '#fff',
    padding: '10px 14px',
    borderRadius: 12,
    cursor: 'pointer',
    fontWeight: 700,
    boxShadow: '0 6px 18px rgba(0,0,0,0.25)'
  },
  backHover: { transform:'translateY(-1px) scale(1.02)', boxShadow:'0 8px 20px rgba(0,0,0,0.30)' },
  headerTitle: {
    margin: 0,
    fontSize: 'clamp(15px, 3.8vw, 22px)',
    fontWeight: 900,
    letterSpacing: '0.2px',
    color: '#DAA520',
    fontFamily: '"Cinzel Decorative","Cinzel",serif',
    whiteSpace: 'nowrap',
    overflow: 'visible',
    textOverflow: 'clip',
    lineHeight: 1.15,
    flex: 1
  },
  headerRight: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    textAlign: 'right',
    pointerEvents: 'none',
    paddingLeft: 8,
  },
  savedFlash: { fontSize: 12, color:'rgba(255,255,255,0.9)' },

  card: {
    width: '100%',
    background: 'rgba(255,255,255,0.98)',
    borderRadius: 16,
    padding: 18,
    boxShadow: '0 16px 48px rgba(44,13,78,0.18), 0 2px 8px rgba(218,165,32,0.09)',
    backdropFilter: 'blur(2px)',
    border: '1px solid #DAA52022'
  },
  error: { color:'#a00', marginTop:8, fontWeight:600 },

  form: { position:'relative', marginTop: 4, display:'flex', flexDirection:'column', gap: 10 },
  label: { fontWeight: 800, fontSize: 14, color: '#4B0082', marginBottom: 4 },

  labelTitle: {
    display: 'block',
    fontWeight: 900,
    color: '#4B0082',
    fontSize: 14,
    margin: '6px 0 4px'
  },

  inputRow: { position:'relative', display:'flex', alignItems:'center' },
  counter: {
    position:'absolute',
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: 12,
    fontWeight: 800,
    color: '#4B0082',
    background: 'rgba(218,165,32,0.18)',
    border: '1px solid #DAA520',
    borderRadius: 999,
    padding: '2px 8px',
    boxShadow:'0 1px 4px rgba(0,0,0,0.12)'
  },

  input: {
    width: '100%',
    padding: '14px 84px 14px 14px', // espacio para botón emoji + contador
    border: '1px solid #4B008233',
    borderRadius: 12,
    background: '#FFF8E1',
    fontSize: 16,
    boxShadow: '0 1px 6px rgba(218,165,32,0.10)',
    outline: 'none',
    transition: 'border 0.18s, box-shadow 0.18s',
    color: '#2d084c'
  },
  inputFocus: { border:'1px solid #DAA520', boxShadow:'0 0 0 3px rgba(218,165,32,0.25)' },

  // Progress bar under the title
  progressTrack: {
    height: 6,
    width: '100%',
    borderRadius: 999,
    background: 'linear-gradient(90deg, rgba(75,0,130,0.08), rgba(218,165,32,0.08))',
    overflow: 'hidden',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.06)'
  },
  progressBar: {
    height: '100%',
    borderRadius: 999,
    background: 'linear-gradient(90deg, #DAA520, #FFD54F)',
    transition: 'width .15s ease'
  },

  help: { color:'#6a5aa5', fontSize:12, marginTop: 4 },

  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },

  textarea: {
    padding: '14px 16px',
    border: '1px solid #4B008233',
    borderRadius: 12,
    background: '#FFF8E1',
    fontSize: 16,
    boxShadow: '0 1px 6px rgba(218,165,32,0.10)',
    resize: 'none', // usamos auto-grow
    outline: 'none',
    transition: 'border 0.18s, box-shadow 0.18s',
    color: '#2d084c',
    lineHeight: 1.55
  },
  textareaFocus: { border:'1px solid #DAA520', boxShadow:'0 0 0 3px rgba(218,165,32,0.25)' },

  actions: { display:'flex', justifyContent:'flex-end', gap: 10, marginTop: 8 },

  secondaryBtn: {
    border: '1px solid rgba(75,0,130,0.25)',
    background: 'linear-gradient(180deg, #ffffff, #faf5ff)',
    color: '#4B0082',
    fontWeight: 800,
    borderRadius: 10,
    padding: '10px 14px',
    cursor: 'pointer',
    fontSize: 14,
    boxShadow: '0 1px 8px rgba(75,0,130,0.12)',
  },
  secondaryBtnDisabled: { opacity: 0.6, cursor: 'not-allowed' },

  btn: {
    border: '1px solid #4B008222',
    background: 'linear-gradient(90deg, #DAA520 0%, #FFD54F 100%)',
    color: '#fff',
    fontWeight: 800,
    borderRadius: 12,
    padding: '12px 18px',
    cursor: 'pointer',
    fontSize: 16,
    boxShadow: '0 2px 16px rgba(218,165,32,0.18), 0 1.5px 0px #4B0082',
    transition: 'background 0.15s, transform 0.13s, box-shadow 0.13s',
    letterSpacing: '0.4px',
    transform: 'translateY(0)'
  },
  btnHover: { transform:'translateY(-1px)', boxShadow:'0 4px 22px rgba(218,165,32,0.28), 0 1.5px 0px #4B0082' },
  btnDisabled: { filter:'grayscale(0.2) brightness(0.98)', opacity:0.7, cursor:'not-allowed' },

  bodyMetaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 6,
  },
  bodyHelp: { color:'#6a5aa5', fontSize:12 },
  bodyCounter: {
    fontSize: 12,
    fontWeight: 800,
    color: '#4B0082',
    background: 'rgba(218,165,32,0.18)',
    border: '1px solid #DAA520',
    borderRadius: 999,
    padding: '2px 8px',
    boxShadow:'0 1px 4px rgba(0,0,0,0.12)'
  },
  bodyCounterLow: { background:'rgba(239,68,68,0.10)', borderColor:'#ef4444', color:'#b91c1c' },
  bodyCounterWarn: { background:'rgba(239,68,68,0.18)', borderColor:'#dc2626', color:'#991b1b' },
};
